import React, { useEffect, useState, useRef, useCallback } from "react";
import "./Row.css";
import AmazonAd from "./components/AmazonAd";

function Row({ title, fetchFunction, onItemClick, type = 'book', isFirstRow = false, adPosition = null, isPremium = false }) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const rowRef = useRef(null);
  const isResetting = useRef(false);
  const scrollTimeout = useRef(null);

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const results = await fetchFunction();
        setItems(results || []);
      } catch (error) {
        console.error(`Error fetching ${title}:`, error);
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    }

    if (fetchFunction) {
      fetchData();
    }
  }, [fetchFunction, title]);

  // Initialize scroll position to center (for infinite effect)
  useEffect(() => {
    if (rowRef.current && items.length > 0) {
      const container = rowRef.current;
      requestAnimationFrame(() => {
        const oneThird = container.scrollWidth / 3;
        container.style.scrollBehavior = 'auto';
        container.scrollLeft = oneThird;
        container.style.scrollBehavior = 'smooth';
      });
    }
  }, [items]);

  const handleItemClick = (item) => {
    if (onItemClick) {
      onItemClick({ ...item, type });
    }
  };

  // Reset scroll position for infinite loop
  const resetScrollIfNeeded = useCallback(() => {
    if (isResetting.current || !rowRef.current) return;

    const container = rowRef.current;
    const scrollWidth = container.scrollWidth;
    const scrollLeft = container.scrollLeft;
    const oneThird = scrollWidth / 3;
    const maxScroll = scrollWidth - container.clientWidth;

    // Near the end (right side) - jump back
    if (scrollLeft >= oneThird * 2 - 50) {
      isResetting.current = true;
      container.style.scrollBehavior = 'auto';
      container.scrollLeft = scrollLeft - oneThird;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          container.style.scrollBehavior = 'smooth';
          isResetting.current = false;
        });
      });
    }
    // Near the start (left side) - jump forward
    else if (scrollLeft <= oneThird - 50) {
      isResetting.current = true;
      container.style.scrollBehavior = 'auto';
      container.scrollLeft = scrollLeft + oneThird;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          container.style.scrollBehavior = 'smooth';
          isResetting.current = false;
        });
      });
    }
  }, []);

  // Handle scroll end detection
  const handleScrollEnd = useCallback(() => {
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }
    scrollTimeout.current = setTimeout(() => {
      resetScrollIfNeeded();
    }, 100);
  }, [resetScrollIfNeeded]);

  useEffect(() => {
    const container = rowRef.current;
    if (container) {
      container.addEventListener('scroll', handleScrollEnd);
      return () => {
        container.removeEventListener('scroll', handleScrollEnd);
        if (scrollTimeout.current) {
          clearTimeout(scrollTimeout.current);
        }
      };
    }
  }, [handleScrollEnd]);

  const scroll = (direction, e) => {
    e.stopPropagation();
    if (rowRef.current && !isResetting.current) {
      const container = rowRef.current;
      const itemWidth = 176;
      const visibleItems = Math.floor(container.clientWidth / itemWidth);
      const scrollAmount = itemWidth * Math.max(2, visibleItems - 1);

      // First reset if needed, then scroll
      resetScrollIfNeeded();

      setTimeout(() => {
        container.scrollBy({
          left: direction === 'left' ? -scrollAmount : scrollAmount,
          behavior: 'smooth'
        });
      }, 10);
    }
  };

  const getItemIcon = () => {
    if (type === 'audiobook') {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
        </svg>
      );
    }
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z"/>
      </svg>
    );
  };

  // Skeleton loading - fill the entire width
  if (isLoading) {
    return (
      <div className="row">
        <h2 className="row__title">{title}</h2>
        <div className="row__container">
          <div className="row__posters row__posters--skeleton">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="row__poster-skeleton">
                <div className="row__poster-skeleton-image"></div>
                <div className="row__poster-skeleton-title"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  // Create infinite items array (3x original for seamless loop)
  const infiniteItems = [...items, ...items, ...items];

  // Function to render a book/audiobook card
  const renderItemCard = (item, index) => (
    <div
      key={`${item.id}-${index}`}
      className="row__poster-card"
      onClick={() => handleItemClick(item)}
    >
      {item.coverUrl ? (
        <img
          className="row__poster"
          src={item.coverUrl}
          alt={item.title}
          loading={isFirstRow ? "eager" : "lazy"}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      ) : null}
      <div
        className="row__poster-placeholder"
        style={{ display: item.coverUrl ? 'none' : 'flex' }}
      >
        {getItemIcon()}
        <span className="row__poster-placeholder-title">{item.title}</span>
      </div>
      <div className="row__poster-info">
        <span className="row__poster-title">{item.title}</span>
        <span className="row__poster-author">
          {item.authors?.[0] || 'Unknown author'}
        </span>
      </div>
    </div>
  );

  // Render items with ad inserted at specified position
  const renderItemsWithAd = () => {
    const elements = [];
    const itemCount = items.length;

    // Insert ads in all three sections for infinite scroll
    // Ad positions: in first, second (visible), and third sections
    const adPositions = adPosition !== null && !isPremium ? [
      adPosition,                    // First section
      adPosition + itemCount,        // Second section (visible on load)
      adPosition + itemCount * 2     // Third section
    ] : [];

    infiniteItems.forEach((item, index) => {
      // Insert ad at this position if it's an ad position
      if (adPositions.includes(index)) {
        elements.push(
          <AmazonAd key={`ad-${index}`} type="grid" isPremium={isPremium} />
        );
      }
      elements.push(renderItemCard(item, index));
    });

    return elements;
  };

  return (
    <div className="row">
      <h2 className="row__title">{title}</h2>

      <div className="row__container">
        <div
          className="row__chevron row__chevron--left"
          onClick={(e) => scroll('left', e)}
          onMouseDown={(e) => e.preventDefault()}
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 8L12 16L20 24" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        <div className="row__posters" ref={rowRef}>
          {renderItemsWithAd()}
        </div>

        <div
          className="row__chevron row__chevron--right"
          onClick={(e) => scroll('right', e)}
          onMouseDown={(e) => e.preventDefault()}
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8L20 16L12 24" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

export default Row;
